import { create } from "zustand";
import i18n from "../i18n.js";

import {
  fetchGiftsAPI,
  handleSearchAPI,
  fetchGiftDetailsAPI,
  postWPWAPI,
  semanticSearchAPI,
} from "../Services/giftService.js";
import { generatePersonaAPI, addPersonaHistoryAPI } from "../Services/userService.js";
import useAuthStore from "./AuthStore.jsx";
import usePersonaStore from "./PersonaStore.jsx";
import {
  storeUnauthSearchText,
  storeGeneratedPersonaData,
  clearSelectedPersonaId
} from "../Services/personaService.js";
import { updateSearchTextAndURL } from "../Utils/personaSummaryUtils.js";

const getEmailFromToken = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.email || null;
  } catch {
    return null;
  }
};
const getUserLocation = (userInfo) =>
  (userInfo?.["X-countryName"] || "worldwide").toLowerCase();

const cleanSearchText = (html, excludePersonaSummary = false) => {
  // Create a temporary div to parse the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Handle persona summary nodes - convert to {summary} format or remove
  const personaSummaries = tempDiv.querySelectorAll('span[data-type="persona-summary"]');
  personaSummaries.forEach((summary) => {
    if (excludePersonaSummary) {
      // Remove for history tracking
      summary.remove();
    } else {
      // Convert to {summary} format for search
      const summaryText = summary.getAttribute('data-summary') || summary.textContent;
      summary.outerHTML = `${summaryText} `;
    }
  });

  // Get all mention spans
  const mentions = tempDiv.querySelectorAll('span[class*="mention"]');

  // Replace each mention with its appropriate format
  mentions.forEach((mention) => {
    const text = mention.textContent;
    const isEdited = mention.classList.contains("edited");
    const replacement = isEdited ? `{ { {${text} } } } ` : `{${text} } `;
    mention.outerHTML = replacement;
  });

  // Get the final text and remove any paragraph tags
  let cleaned = tempDiv.innerHTML.replace(/<\/?p>/g, "");

  return cleaned;
};

const useAppStore = create((set, get) => {
  const createSetter = (key) => (value) => set({ [key]: value });

  return {
    searchText: "",
    setSearchText: createSetter("searchText"),

    gifts: [],
    setGifts: createSetter("gifts"),

    windowWidth: window.innerWidth,
    setWindowWidth: createSetter("windowWidth"),

    error: null,
    setError: createSetter("error"),

    mounted: false,
    setMounted: createSetter("mounted"),

    editorText: "",
    setEditorText: createSetter("editorText"),

    count: Number(localStorage.getItem("count")) || 0,
    setCount: (count) => {
      set({ count });
      localStorage.setItem("count", count);
    },
    incrementCount: () => {
      const newCount = Number(get().count) + 1;
      set({ count: newCount });
      localStorage.setItem("count", newCount);
    },

    placeholders: [
      "search.placeholders.one",
      "search.placeholders.two",
      "search.placeholders.three",
      "search.placeholders.four",
      "search.placeholders.five",
    ],
    placeholderIndex: 0,
    setPlaceholderIndex: createSetter("placeholderIndex"),
    rotatePlaceholder: () => {
      const nextIndex =
        (get().placeholderIndex + 1) % get().placeholders.length;
      set({ placeholderIndex: nextIndex });
    },

    isFocused: false,
    setIsFocused: createSetter("isFocused"),

    endRef: null,
    storedValue: localStorage.getItem("prompt"),

    show: false,
    setShow: createSetter("show"),

    tag: "",
    setTag: createSetter("tag"),

    isHovered: false,
    setIsHovered: createSetter("isHovered"),

    isExpanded: false,
    setIsExpanded: createSetter("isExpanded"),

    isSearching: false,
    setIsSearching: createSetter("isSearching"),

    searchError: null,
    setSearchError: createSetter("searchError"),

    isEmptyResult: false,
    setisEmptyResult: createSetter("isEmptyResult"),

    isVoiceRecognitionActive: false,
    setIsVoiceRecognitionActive: createSetter("isVoiceRecognitionActive"),

    handleSearch: async (scroll) => {
      set({ isSearching: true });
      const ids = get().gifts.map((gift) => gift._id);
      try {
        await handleSearchAPI(ids);
      } catch (err) {
        set({ searchError: err });
      } finally {
        set({ isSearching: false });
        if (scroll) window.scrollTo(0, 0);
      }
    },

    demoifySearchText: (text) => {
      const modified = text.replace(
        /{([^}]+)}/g,
        '<span data-type="mention" class="mention" data-id="1" data-label="$1">$1</span>'
      );
      const html = `< p > ${modified}</p > `;
      set({ searchText: html });
      return html;
    },

    lastLoggedQuery: null,

    getWantedGifts: async (userInfo, scroll) => {
      set({ isSearching: true });
      const rawText = get().searchText;
      let modifiedText = cleanSearchText(rawText);

      // Get persona store functions and state
      const {
        setPersonaName,
        setShowCreatePersonaForUnauthUser,
        setGeneratedPersonaData,
        setShowGeneratedPersonaAvatar,
        selectedPersona
      } = usePersonaStore.getState();

      // Note: cleanSearchText already handles persona summary nodes and converts them to text
      // No need to manually prepend the summary here as it's already included in modifiedText

      localStorage.setItem("prompt", modifiedText);

      // Track persona search history if a persona is selected and user is authenticated
      const { isAuthenticated } = useAuthStore.getState();
      if (isAuthenticated && selectedPersona && selectedPersona._id) {
        // Exclude persona summary from history tracking
        const cleanedSearchText = cleanSearchText(rawText, true).trim();

        // Only add to history if text is not empty and different from the last history item
        const lastHistoryItem = selectedPersona.history && selectedPersona.history.length > 0
          ? selectedPersona.history[selectedPersona.history.length - 1]
          : null;

        const shouldAddToHistory = cleanedSearchText && cleanedSearchText !== lastHistoryItem;

        if (shouldAddToHistory) {
          // Call the persona history API and update the persona with the full response
          addPersonaHistoryAPI(selectedPersona._id, cleanedSearchText, i18n.language)
            .then(async response => {
              if (response.success && response.data && response.data.persona) {
                // Update the entire persona with the full data from API response
                const { updatePersona } = usePersonaStore.getState();
                updatePersona(response.data.persona);

                // Update search text and URL with the updated persona summary
                const updatedPersona = response.data.persona;
                await updateSearchTextAndURL(updatedPersona, (text) => {
                  set({ searchText: text });
                });
              }
            })
            .catch(error => {
              console.error("Failed to track persona search history:", error);
            });
        }
      }

      try {
        const userLocation = getUserLocation(userInfo);

        // Check if user is authenticated
        const { isAuthenticated } = useAuthStore.getState();

        let giftsData, personaData;

        // Log search for all users (authenticated and unauthenticated), deduplicated
        if (get().lastLoggedQuery !== modifiedText) {
          set({ lastLoggedQuery: modifiedText });
          const emailToLog = getEmailFromToken() || useAuthStore.getState().email || "guest";
          postWPWAPI(modifiedText, { ...userInfo, email: emailToLog }).catch(() => { });
        }

        if (isAuthenticated) {
          // For authenticated users, conditionally generate persona only if none is selected
          const apiCalls = [
            semanticSearchAPI(modifiedText),
            postWPWAPI(modifiedText, userInfo),
          ];

          // Only generate persona if no persona is selected (check both state and localStorage)
          // AND if we're not currently creating a persona (to prevent race conditions)
          const { getSelectedPersonaId } = await import("../Services/personaService.js");
          const storedPersonaId = getSelectedPersonaId();
          const hasPersonaSelected = selectedPersona || storedPersonaId;

          // Check if a persona is being created to prevent race condition
          const PersonaStore = await import("./PersonaStore.jsx");
          const { isCreatingPersona } = PersonaStore.default.getState();

          if (!hasPersonaSelected && !isCreatingPersona) {
            apiCalls.push(generatePersonaAPI(modifiedText));
          }

          const results = await Promise.all(apiCalls);
          giftsData = results[0];
          personaData = results[1]; // Will be undefined if persona is selected or being created

          // For authenticated users with no selected persona, show the generated persona avatar
          // Check both state and localStorage to prevent showing when persona is being restored
          // Also check if persona is being created to prevent race condition
          if (personaData && personaData.persona && !hasPersonaSelected && !isCreatingPersona) {
            setGeneratedPersonaData(personaData);
            setShowGeneratedPersonaAvatar(true);
            // Clear any stored persona ID since we're showing a generated one
            clearSelectedPersonaId();
          }
        } else {
          // For unauthenticated users, only fetch gifts and generate persona
          // Store the search text in localStorage to use after authentication
          storeUnauthSearchText(modifiedText);

          [giftsData, personaData] = await Promise.all([
            semanticSearchAPI(modifiedText),
            // Generate persona suggestion (works without auth)
            generatePersonaAPI(modifiedText)
          ]);

          // Show the create persona component for unauthenticated users
          if (personaData && personaData.persona) {
            setShowCreatePersonaForUnauthUser(true);
            setGeneratedPersonaData(personaData);
            setShowGeneratedPersonaAvatar(true); // Enable unified avatar UI
            // Store the generated persona data in localStorage
            storeGeneratedPersonaData(personaData);
          }
        }

        // Set persona name from API response (for both authenticated and unauthenticated users)
        if (personaData && personaData.persona) {
          setPersonaName(personaData.persona);
        }

        console.log("Initial gifts data:", giftsData);
        const mapGifts = (data) => data.map((gift) => ({
          _id: gift.mid || gift._id,
          opId: gift._id,
          title: gift.title,
          link: gift.link,
          price: gift.price,
          img: gift.img,
          country: gift.country,
          loveMeter: gift.loveMeter,
          categories: gift.categories,
          categoriesTranslated: gift.categoriesTranslated || null,
          description: gift.description,
          explanation: gift["why this gift"] || gift.why_this_gift || null,
          translations: gift.translations || null,
        }));

        const initialResult = mapGifts(giftsData);
        set({ gifts: initialResult, isEmptyResult: initialResult.length === 0 });
        await get().handleSearch(scroll);

        // Secondary Request: Get "Why this gift?" explanations
        if (initialResult.length > 0) {
          try {
            const explainPayload = {
              event_type: "explain_results",
              query: modifiedText,
              hits: giftsData, // the raw response from the first api hit
              country: userLocation
            };

            const explainResponse = await fetchGiftsAPI(explainPayload);
            console.log("Explanations response:", explainResponse);

            if (explainResponse && Array.isArray(explainResponse)) {
              const updatedResult = mapGifts(explainResponse);
              set({ gifts: updatedResult });
              // We do NOT call handleSearch(scroll) here to avoid the scroll-to-top and duplicate analytics
            }
          } catch (explainErr) {
            console.error("Failed to fetch explanations:", explainErr);
          }
        }
      } catch (err) {
        set({ searchError: err });
      }
    },

    getSuggestedGifts: async (
      userInfo,
      titles,
      eventName,
      eventDescription
    ) => {
      console.log(titles, eventName, eventDescription);
      set({ isSearching: true });
      var searchText = " " + titles + " " + eventName + " " + eventDescription;
      try {
        const userLocation = getUserLocation(userInfo);
        const [giftsData] = await Promise.all([
          semanticSearchAPI(searchText),
          postWPWAPI(titles, userInfo),
        ]);

        const result = giftsData.map((gift) => ({
          _id: gift.mid || gift._id,
          opId: gift._id,
          title: gift.title,
          link: gift.link,
          price: gift.modified_price ? Math.ceil(gift.modified_price) : 0,
          img: gift.img,
          country: gift.country,
          loveMeter: gift.loveMeter,
          categories: gift.categories,
          categoriesTranslated: gift.categoriesTranslated || null,
          description: gift.description,
          translations: gift.translations || null,
        }));

        set({ gifts: result, isEmptyResult: result.length === 0 });
      } catch (err) {
        set({ searchError: err });
      } finally {
        set({ isSearching: false });
      }
    },
  };
});

export default useAppStore;